import React, { FormEvent, useEffect, useRef, useState } from "react";
import { Popover } from "antd";

interface ContentEditableProps {
    captions: string;
    className?: string;
    style?: React.CSSProperties;
    handleInput: (text: string, focused: boolean) => void;
    handleBlur: (text: string) => void;
    editing?: string;
    contentEditable?: boolean;
    handleClick?: () => void;
    comment?: string;
    ariaLabel?: string;
    /** Base direction of the caption text. Must be set for RTL languages, otherwise the
     *  browser lays Arabic out with an LTR base and the translator sees misplaced
     *  punctuation and reordered Latin runs while typing. */
    dir?: "ltr" | "rtl";
}

export const ContentEditable: React.FC<ContentEditableProps> = ({
    captions,
    handleInput,
    handleBlur,
    className,
    style,
    contentEditable,
    handleClick,
    comment,
    ariaLabel,
    dir,
}) => {
    const [focused, setFocused] = useState(false);
    const divRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const div = divRef.current;
        if (!div) return;

        const handleTouchStart = (e: TouchEvent) => {
            // Check if content is scrollable
            const isScrollable = div.scrollHeight > div.clientHeight;
            if (isScrollable) {
                // Stop parent from scrolling when touching this element
                e.stopPropagation();
            }
        };

        const handleTouchMove = (e: TouchEvent) => {
            const isScrollable = div.scrollHeight > div.clientHeight;
            if (isScrollable) {
                // Allow this element to scroll, prevent parent scroll
                e.stopPropagation();
            }
        };

        div.addEventListener("touchstart", handleTouchStart, { passive: true });
        div.addEventListener("touchmove", handleTouchMove, { passive: true });

        return () => {
            div.removeEventListener("touchstart", handleTouchStart);
            div.removeEventListener("touchmove", handleTouchMove);
        };
    }, []);
    return (
        <Popover
            content={comment && <div>{comment}</div>}
            title={comment && "Comment"}
            trigger="click"
        >
            <div
                ref={divRef}
                aria-label={ariaLabel}
                className={className}
                dir={dir}
                contentEditable={contentEditable}
                suppressContentEditableWarning={true}
                onFocus={() => {
                    setFocused(true);
                    handleClick?.();
                }}
                onInput={(e: FormEvent<HTMLDivElement>) =>
                    handleInput((e.target as HTMLTextAreaElement).innerText, focused)
                }
                onBlur={(e) => {
                    setFocused(false);
                    handleBlur(e.target.innerText);
                }}
                style={{
                    ...style,
                    WebkitOverflowScrolling: "touch", // Smooth scrolling on iOS
                    touchAction: "pan-y", // Allow vertical scrolling only
                }}
            >
                {captions}
            </div>
        </Popover>
    );
};
